import { Dialog, DialogContent } from "@/components/podkit/dialog/Dialog";
import { cn } from "@/components/podkit/lib/cn";
import { type TrackLocation } from "@/hooks/use-segment";
import {
    activeTabAtom,
    escapePressCallbackAtom,
    TabsValues,
} from "@/routes/environments/create/create-environment-utils";
import { CreateEnvironmentDetailsLayout } from "@/routes/environments/create/CreateEnvironmentDetailsLayout";
import { useAtom, useAtomValue } from "jotai";
import { useEffect, useState, type FC } from "react";
import { useNavigate } from "react-router-dom";

export const CreateEnvironmentModal: FC<{
    open: boolean;
    onClose: () => void;
    disableProjectSelection?: boolean;
    "data-track-location": TrackLocation;
    initialAutoStart?: boolean;
}> = ({ open, onClose, disableProjectSelection, "data-track-location": dataTrackLocation, initialAutoStart }) => {
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useAtom(activeTabAtom);
    const [autoStart, setAutoStart] = useState<boolean>();

    useEffect(() => {
        if (initialAutoStart) {
            // basically lock the auto start to the initial value if it's true. We do this because we clear the search params when the modal initializes
            setAutoStart(initialAutoStart);
        }
    }, [initialAutoStart]);

    const onOpenChange = (open: boolean) => {
        if (!open) {
            onClose();
            navigate({ hash: "" }, { replace: true });
            setActiveTab(undefined);
        }
    };

    const escapePressCallback = useAtomValue(escapePressCallbackAtom);
    const effectiveAutoStart = autoStart;

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                data-track-location={dataTrackLocation}
                className={cn(
                    "mt-8 h-auto w-full max-w-[600px] self-start md:mt-28",
                )}
                data-testid="create-environment-modal"
                onEscapeKeyDown={(e) => {
                    if (escapePressCallback) {
                        e.preventDefault();
                        escapePressCallback.fn();
                        return;
                    }

                    if (activeTab !== TabsValues.home && activeTab !== undefined) {
                        e.preventDefault();
                        setActiveTab(TabsValues.home);
                    }
                }}
            >
                <CreateEnvironmentDetailsLayout
                    onClose={onClose}
                    initialContextURL={""}
                    disableProjectSelection={disableProjectSelection}
                    autoStart={effectiveAutoStart}
                />
            </DialogContent>
        </Dialog>
    );
};
